import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, EmptyState, Pill, Segments, useToast, type PillTone } from "@signet/ui";
import { ago, compact, CreatorAvatar, type CreatorPhotos } from "@signet/web-kit";
import { APPEAL_STATE, APPEAL_STATES, appealOpen, type AppealState } from "../../shared/appeal.ts";
import { appealUpdate, fetchAppealNotes, shortDate, stockKeys, type AppealNote, type Stock } from "./api.ts";
import { isThreeReason } from "./status.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   3 reason bans and their appeals (Jay's boss, 2026-10-15): "the three
   reason ban is the most important ban that there is".

   The first ban on a phone is a 3 reason ban, appealed at once. The other
   accounts on the phone can follow it as connected bans, and come back only
   if it does. So each 3 reason ban carries a status and dated updates, and
   lists the connected bans hanging off it with the followers riding on it,
   for deciding whether it is worth paying someone to get it back.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

export const APPEAL_TONE: Record<AppealState, PillTone> = { appealed: "warn", in_appeal: "warn", asked_for_id: "info", came_back: "good", not_back: "bad" };

export function AppealPill({ state }: { state: AppealState | null }) {
  return state ? <Pill tone={APPEAL_TONE[state]} dot>{APPEAL_STATE[state]}</Pill> : <Pill tone="idle">No update yet</Pill>;
}

/** The connected bans that came from this 3 reason ban. */
export const connectedTo = (a: Stock, all: Stock[]) => all.filter((c) => c.causedBy?.id === a.id);

/** Its own followers and its connected bans', at their last read. Null when none was ever read. */
export function atStake(a: Stock, connected: Stock[]): number | null {
  const counts = [a, ...connected].map((x) => x.followers).filter((n): n is number => n !== null);
  return counts.length ? counts.reduce((sum, n) => sum + n, 0) : null;
}

function noteLine(n: AppealNote) {
  return [n.by, ago(n.at)].filter(Boolean).join(", ");
}

/** In the account drawer: where the appeal stands, an update box, its connected bans and every update so far. */
export function AppealPanel({ account, all, canAct, onOpen }: { account: Stock; all: Stock[]; canAct: boolean; onOpen: (id: string) => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<AppealState | null>(null);
  const [note, setNote] = useState("");
  const connected = connectedTo(account, all);
  const stillBanned = connected.filter((c) => c.status === "banned");
  const [back, setBack] = useState<string[] | null>(null);
  const bringBack = back ?? stillBanned.map((c) => c.id);
  const notes = useQuery({ queryKey: [...stockKeys.all, "appeal", account.id], queryFn: () => fetchAppealNotes(account.id) });
  const save = useMutation({
    mutationFn: () => appealUpdate(account.id, { state: state && state !== account.appealState ? state : null, note: note.trim() || null, bringBack: state === "came_back" ? bringBack : [] }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(state === "came_back" ? `@${account.username} is back` : `Update saved on @${account.username}`);
      setState(null);
      setNote("");
      setBack(null);
    },
    onError: (error) => toast.error("Couldn't save the update", messageOf(error)),
  });
  const total = atStake(account, connected);
  const changed = (state && state !== account.appealState) || note.trim();

  return (
    <section className="igs-appeal">
      <h3>
        Appeal <AppealPill state={account.appealState} />
      </h3>
      {account.appealFrom && account.status !== "banned" ? <p className="faint">3 reason ban on {shortDate(account.appealFrom)}.</p> : null}

      {canAct ? (
        <div className="igs-appeal-form">
          <div className="igs-appeal-states" role="radiogroup" aria-label="Where the appeal stands">
            {APPEAL_STATES.map((s) => {
              const on = (state ?? account.appealState) === s;
              return (
                <button key={s} type="button" role="radio" aria-checked={on} className={`igs-appeal-state ${s}${on ? " on" : ""}`} onClick={() => setState(s)}>
                  {APPEAL_STATE[s]}
                </button>
              );
            })}
          </div>
          {state === "came_back" && account.appealState !== "came_back" && stillBanned.length ? (
            <div className="igs-appeal-back">
              <p>Its connected bans come back with it. Untick any that didn't:</p>
              {stillBanned.map((c) => (
                <label key={c.id} className="igs-appeal-back-row">
                  <Checkbox checked={bringBack.includes(c.id)} label={`@${c.username} came back too`} onChange={(on) => setBack(on ? [...bringBack, c.id] : bringBack.filter((x) => x !== c.id))} />
                  @{c.username}
                </label>
              ))}
            </div>
          ) : null}
          {state === "not_back" && account.appealState !== "not_back" ? (
            <p className="igs-appeal-off">
              It comes off phone {account.phone} and goes to the Graveyard{stillBanned.length ? `, with ${stillBanned.map((c) => `@${c.username}`).join(", ")}` : ""}, and counts as banned in Best and worst setups.
            </p>
          ) : null}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Checked today, still in appeal" aria-label="Note" />
          <button type="button" className="button brass" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save update"}
          </button>
        </div>
      ) : null}

      <div className="igs-appeal-stake">
        <div>
          <span>Followers at stake</span>
          <strong className="tabular">{total === null ? "-" : compact(total)}</strong>
          <small className="faint">its own plus its connected bans', at their last read</small>
        </div>
      </div>

      <h4>Connected bans from it</h4>
      {connected.length ? (
        <ul className="igs-appeal-connected">
          {connected.map((c) => (
            <li key={c.id}>
              <button type="button" className="igs-inline-link" onClick={() => onOpen(c.id)}>@{c.username}</button>
              <span className="tabular">{c.followers === null ? "-" : `${compact(c.followers)} followers`}</span>
              <span className="faint">{c.status === "banned" ? "banned" : "back"}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="faint">None linked to it. A connected ban is linked when it is marked banned.</p>
      )}

      <h4>Updates</h4>
      {notes.data?.notes.length ? (
        <ol className="igs-appeal-notes">
          {notes.data.notes.map((n) => (
            <li key={n.id}>
              <div>
                {n.state ? <AppealPill state={n.state} /> : null}
                <span className="faint">{noteLine(n)}</span>
              </div>
              {n.note ? <p>{n.note}</p> : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="faint">{notes.isLoading ? "Loading…" : "No updates yet."}</p>
      )}
    </section>
  );
}

type Filter = "open" | "not_back" | "came_back";

/** The Accounts tab's 3 reason bans view: every one, the open appeals first. */
export function AppealsView({ accounts, photos, onOpen }: { accounts: Stock[]; photos: CreatorPhotos; onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<Filter>("open");
  const bans = accounts.filter(isThreeReason);
  const groups: Record<Filter, Stock[]> = {
    open: bans.filter((a) => appealOpen(a.appealState)),
    not_back: bans.filter((a) => a.appealState === "not_back"),
    came_back: bans.filter((a) => a.appealState === "came_back"),
  };
  // Open ones: the longest without an update first, since those need a look. The rest: newest ban first.
  const lastTouch = (a: Stock) => a.lastNote?.at ?? `${a.appealFrom ?? a.statusOn ?? ""}T00:00:00`;
  const shown = [...groups[filter]].sort((a, b) =>
    filter === "open" ? lastTouch(a).localeCompare(lastTouch(b)) : (b.appealFrom ?? b.statusOn ?? "").localeCompare(a.appealFrom ?? a.statusOn ?? ""),
  );
  const options: { value: Filter; label: string }[] = [
    { value: "open", label: `Waiting (${groups.open.length})` },
    { value: "not_back", label: `Didn't come back (${groups.not_back.length})` },
    { value: "came_back", label: `Came back (${groups.came_back.length})` },
  ];

  return (
    <section className="igs-appeals">
      <div className="igs-appeals-head">
        <p>The first ban on a phone is a 3 reason ban. Its connected bans come back only if it does, so each one keeps where its appeal stands and every update.</p>
        <div className="igs-appeals-filter">
          <Segments label="Which appeals" value={filter} options={options} onChange={setFilter} />
        </div>
      </div>
      {shown.length ? (
        <ul className="igs-appeals-list">
          {shown.map((a) => {
            const connected = connectedTo(a, accounts);
            const total = atStake(a, connected);
            const from = a.appealFrom ?? a.statusOn;
            return (
              <li key={a.id}>
                <button type="button" className="igs-appeals-row" onClick={() => onOpen(a.id)}>
                  <span className="igs-appeals-who">
                    {a.creator ? <CreatorAvatar name={a.creator} src={photos(a.creator)} size={30} /> : null}
                    <span>
                      <strong>@{a.username}</strong>
                      <small>{[a.posterName ? `${a.posterName}'s phone ${a.phone ?? ""}`.trim() : a.phone ? `Phone ${a.phone}` : null, from ? `banned ${shortDate(from)}` : null].filter(Boolean).join(", ")}</small>
                    </span>
                  </span>
                  <span className="igs-appeals-state"><AppealPill state={a.appealState} /></span>
                  <span className="igs-appeals-note">
                    {a.lastNote ? (
                      <>
                        {a.lastNote.note ? <span>{a.lastNote.note}</span> : <span className="faint">{a.lastNote.state ? APPEAL_STATE[a.lastNote.state] : "Update"}</span>}
                        <small className="faint">{noteLine(a.lastNote)}</small>
                      </>
                    ) : (
                      <span className="faint">No updates yet</span>
                    )}
                  </span>
                  <span className="igs-appeals-connected">
                    {connected.length ? connected.map((c) => <span key={c.id}>@{c.username}</span>) : <span className="faint">No connected bans</span>}
                  </span>
                  <span className="igs-appeals-stake tabular">
                    <strong>{total === null ? "-" : compact(total)}</strong>
                    <small className="faint">followers at stake</small>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title={filter === "open" ? "No appeals waiting" : filter === "not_back" ? "None lost so far" : "None back yet"}
          body={filter === "open" ? "A 3 reason ban shows here as soon as it's marked banned." : "3 reason bans show here once their appeal is settled."}
        />
      )}
    </section>
  );
}
